import { useState } from "react";

function LeagueCard({ league, onOpen, onDelete, isAdmin, userId, isDeleting }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const getTournamentBadge = (type) => {
    if (type === "WorldCup") {
      return {
        label: "🏆 World Cup",
        bgColor: "bg-amber-50",
        textColor: "text-amber-700",
        borderColor: "border-amber-200",
      };
    }
    return {
      label: "⚽ Premier League",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-700",
      borderColor: "border-emerald-200",
    };
  };

  const badge = getTournamentBadge(league.tournamentType);
  const isCreator = userId === league.createdByUserId;
  const canDelete = isAdmin && isCreator;

  const handleCopyShareLink = () => {
    const shareUrl = `${window.location.origin}/join/${league.inviteCode}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteClick = async () => {
    if (onDelete && canDelete) {
      await onDelete(league.id);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <div className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200 transition hover:-translate-y-0.5 hover:shadow-md">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h4 className="text-lg font-semibold tracking-tight text-slate-900">
              {league.name}
            </h4>
            <p className="mt-1 text-sm text-slate-500">Private league</p>
          </div>

          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-700">
            {league.inviteCode}
          </span>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${badge.bgColor} ${badge.textColor} border ${badge.borderColor}`}>
            {badge.label}
          </span>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onOpen(league)}
            className="flex-1 inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
          >
            Open League
          </button>

          <button
            onClick={() => setShowShareModal(true)}
            className="inline-flex items-center justify-center rounded-2xl bg-blue-50 px-3 py-3 text-sm font-semibold text-blue-600 border border-blue-200 transition hover:bg-blue-100"
            title="Share league"
          >
            🔗
          </button>

          {canDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              className="inline-flex items-center justify-center rounded-2xl bg-red-50 px-3 py-3 text-sm font-semibold text-red-600 border border-red-200 transition hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete league"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-lg">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Share League
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              Send this link to friends to invite them to join "{league.name}"
            </p>
            <div className="mb-4 p-3 bg-slate-50 rounded-2xl border border-slate-200">
              <p className="text-xs font-mono text-slate-600 break-all">
                {`${window.location.origin}/join/${league.inviteCode}`}
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 rounded-2xl border border-slate-300 bg-white text-slate-800 font-semibold text-sm transition hover:bg-slate-50"
              >
                Close
              </button>
              <button
                onClick={handleCopyShareLink}
                className="px-4 py-2 rounded-2xl bg-blue-600 text-white font-semibold text-sm transition hover:bg-blue-700"
              >
                {copied ? "✓ Copied!" : "📋 Copy Link"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-lg">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl flex-shrink-0">⚠️</span>
              <h3 className="text-lg font-semibold text-red-600">
                Delete League?
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              This will permanently delete the league "{league.name}" and all associated predictions.
            </p>
            <div className="mb-4 p-3 bg-red-50 rounded-2xl border border-red-200">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                ⚠️ This action cannot be undone
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 rounded-2xl border border-slate-300 bg-white text-slate-800 font-semibold text-sm transition hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="px-4 py-2 rounded-2xl bg-red-600 text-white font-semibold text-sm transition hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LeagueCard;
